import React from 'react';
import { Link, Navigate, Outlet } from 'react-router';
import { STUDIO_COPY } from '../config';
import { useStudioAuth } from '../hooks/useStudioAuth';

/**
 * Authed Studio routes: brief spinner while the session resolves, Connect on
 * a confirmed signed-out/401, error+retry if `/api/me` hangs or fails.
 * Never leaves a signed-out visitor on an eternal spinner.
 */
const StudioAuthGate: React.FC = () => {
  const { status, isLoggedOut, resolveTimedOut, authError, retryAuth } =
    useStudioAuth();

  if (status === 'loading' && !resolveTimedOut) {
    return (
      <div
        className="flex justify-center py-16"
        data-testid="studio-auth-loading"
      >
        <div className="studio-spinner" />
      </div>
    );
  }

  if (resolveTimedOut || authError) {
    return (
      <div
        className="studio-panel space-y-3"
        role="alert"
        data-testid="studio-auth-error"
      >
        <p>{authError || STUDIO_COPY.authResolveFailed}</p>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="studio-btn-ghost"
            onClick={() => void retryAuth()}
            data-testid="studio-auth-retry"
          >
            {STUDIO_COPY.tryAgain}
          </button>
          <Link
            to="/studio/connect"
            className="studio-btn-primary no-underline"
            data-testid="studio-auth-signin"
          >
            {STUDIO_COPY.connectCta}
          </Link>
        </div>
      </div>
    );
  }

  if (isLoggedOut) {
    return <Navigate to="/studio/connect" replace />;
  }

  return <Outlet />;
};

export default StudioAuthGate;
